/*
This hook ensures proper positioning of elements to prevent overlapping in scenarios where a Sticky Footer, Popup,
and Recent Stores Flyout (in any combination) are present on the page.
1. If a Sticky Footer and Popup are rendered, the Popup should be repositioned above the footer to avoid overlapping.
2. If the Popup overlaps the Recent Stores Flyout, the flyout should be moved upward to prevent overlapping.
   a. Periodically check if the Recent Stores Flyout is rendered, and update its position
      to ensure it does not overlap with the Popup.
   b. When the Sticky Footer is rendered and the Popup is repositioned,
      wait for the Popup's transition to complete (`transitionend`) before re-evaluating the flyout's position.
*/

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';

const stickyFooterSelectors = [
  '.mn_stickyFooterDesktopWrap.mn_stickyFooterHasContent',
  '.mn_stickyFooterVendorWrap',
  '.mn_stickyFooter.mn_stickyFooterHasContent', // CanJs selector
];

export const useAdjustPosition = (popupRef) => {
  const isMobileMQ = useSelector(selectIsMobileMQ);

  // 1
  useEffect(() => {
    const popup = popupRef.current;

    if (!popup || isMobileMQ) return () => {};

    const MAX_ATTEMPTS = 10;
    const INTERVAL = 1000;
    let attempts = 0;

    const timerId = setInterval(() => {
      const stickyFooterElement =
        document.querySelector(stickyFooterSelectors.join(', '));

      if (stickyFooterElement) {
        popup.style.transform = 'translateY(-110px)';
        clearInterval(timerId);
      }

      attempts += 1;
      attempts > MAX_ATTEMPTS && clearInterval(timerId);
    }, INTERVAL);

    return () => {
      popup.style.transform = '';
      clearInterval(timerId);
    };
  }, [popupRef, isMobileMQ]);

  // 2
  useEffect(() => {
    const popup = popupRef.current;

    const adjustFlyoutPosition = () => {
      const flyout = document.querySelector('.mn_recentStoresFlyout');

      if (!flyout) return false;

      const GAP = 20;
      const { top: popupTop } = popup.getBoundingClientRect();
      const { bottom: flyoutBottom } = flyout.getBoundingClientRect();

      if (flyoutBottom > popupTop - GAP) {
        const styles = window.getComputedStyle(flyout);
        const currentMarginBottom = parseFloat(styles.marginBottom) || 0;

        const marginBottom = flyoutBottom - popupTop + GAP;
        flyout.style.marginBottom = `${currentMarginBottom + marginBottom}px`;
        return true;
      }

      return false;
    };

    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'transform') { adjustFlyoutPosition(); }
    };

    if (!popup || isMobileMQ) return () => {};

    // 2a
    const MAX_ATTEMPTS = 10;
    const INTERVAL = 1000;
    let attempts = 0;
    const timerId = setInterval(() => {
      const isCompleted = adjustFlyoutPosition();

      if (isCompleted || attempts > MAX_ATTEMPTS) {
        clearInterval(timerId);
        return;
      }

      attempts += 1;
    }, INTERVAL);

    // 2b
    popup?.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      clearInterval(timerId);
      popup?.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [popupRef, isMobileMQ]);
};
